"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Loader = void 0;
const nanoevents_1 = require("nanoevents");
const Sound_1 = require("./Sound");
const Util_1 = require("./Util");
const Tga_1 = require("./Parsers/Tga");
const Wad_1 = require("./Parsers/Wad");
const Replay_1 = require("./Replay/Replay");
const Sprite_1 = require("./Parsers/Sprite");
const Xhr_1 = require("./Xhr");
const BspParser_1 = require("./Parsers/BspParser");
const TauriLoader_1 = require("./TauriLoader");
var LoadItemStatus;
(function (LoadItemStatus) {
    LoadItemStatus[LoadItemStatus["Loading"] = 1] = "Loading";
    LoadItemStatus[LoadItemStatus["Skipped"] = 2] = "Skipped";
    LoadItemStatus[LoadItemStatus["Error"] = 3] = "Error";
    LoadItemStatus[LoadItemStatus["Done"] = 4] = "Done";
})(LoadItemStatus || (LoadItemStatus = {}));
class LoadItemBase {
    constructor(name) {
        this.name = name;
        this.progress = 0;
        this.status = LoadItemStatus.Loading;
        this.data = null;
    }
    isLoading() {
        return this.status === LoadItemStatus.Loading;
    }
    skip() {
        this.status = LoadItemStatus.Skipped;
    }
    isSkipped() {
        return this.status === LoadItemStatus.Skipped;
    }
    error() {
        this.status = LoadItemStatus.Error;
    }
    isError() {
        return this.status === LoadItemStatus.Error;
    }
    done(data) {
        this.status = LoadItemStatus.Done;
        this.data = data;
    }
    isDone() {
        return this.status === LoadItemStatus.Done;
    }
}
class LoadItemReplay extends LoadItemBase {
    constructor() {
        super(...arguments);
        this.type = 'replay';
    }
}
class LoadItemBsp extends LoadItemBase {
    constructor() {
        super(...arguments);
        this.type = 'bsp';
    }
}
class LoadItemSky extends LoadItemBase {
    constructor() {
        super(...arguments);
        this.type = 'sky';
    }
}
class LoadItemWad extends LoadItemBase {
    constructor() {
        super(...arguments);
        this.type = 'wad';
    }
}
class LoadItemSound extends LoadItemBase {
    constructor() {
        super(...arguments);
        this.type = 'sound';
    }
}
class LoadItemSprite extends LoadItemBase {
    constructor() {
        super(...arguments);
        this.type = 'sprite';
    }
}
class Loader {
    constructor(config) {
        this.localResourceRoot = '';
        this.sprites = {};
        this.config = config;
        this.replay = undefined;
        this.map = undefined;
        this.skies = [];
        this.wads = [];
        this.sounds = [];
        this.events = (0, nanoevents_1.createNanoEvents)();
        this.events.on('error', (err) => {
            console.error(err);
        });
    }
    clear() {
        this.replay = undefined;
        this.map = undefined;
        this.skies.length = 0;
        this.wads.length = 0;
        this.sounds.length = 0;
        this.sprites = {};
    }
    setLocalResourceRoot(path) {
        this.localResourceRoot = path.replace(/[\\/]$/, '');
    }
    checkStatus() {
        if (this.replay && !this.replay.isDone()) {
            return;
        }
        if (this.map && !this.map.isDone()) {
            return;
        }
        for (let i = 0; i < this.skies.length; ++i) {
            if (this.skies[i].isLoading()) {
                return;
            }
        }
        for (let i = 0; i < this.wads.length; ++i) {
            if (this.wads[i].isLoading()) {
                return;
            }
        }
        for (let i = 0; i < this.sounds.length; ++i) {
            if (this.sounds[i].isLoading()) {
                return;
            }
        }
        const sprites = Object.entries(this.sprites);
        for (let i = 0; i < sprites.length; ++i) {
            if (sprites[i][1].isLoading()) {
                return;
            }
        }
        this.events.emit('loadall', this);
    }
    load(name) {
        const extension = (0, Util_1.extname)(name);
        if (extension === '.dem' || extension === '.rec') {
            this.loadReplay(name);
        }
        else if (extension === '.bsp') {
            this.loadMap(name);
        }
        else {
            this.events.emit('error', 'Invalid file extension', name);
        }
    }
    loadFromPath(filePath) {
        return __awaiter(this, void 0, void 0, function* () {
            const extension = (0, Util_1.extname)(filePath);
            if (extension === '.dem' || extension === '.rec') {
                yield this.loadReplayFromPath(filePath);
            }
            else if (extension === '.bsp') {
                yield this.loadMapFromPath(filePath);
            }
            else {
                this.events.emit('error', 'Invalid file extension', filePath);
            }
        });
    }
    loadReplayFromPath(filePath) {
        return __awaiter(this, void 0, void 0, function* () {
            const name = filePath.split(/[/\\]/).pop() || filePath;
            this.replay = new LoadItemReplay(name);
            this.events.emit('loadstart', this.replay);
            try {
                this.replay.progress = 0.5;
                this.events.emit('progress', this.replay);
                const buffer = yield (0, TauriLoader_1.readFileBytes)(filePath);
                this.replay.progress = 1;
                this.events.emit('progress', this.replay);
                const replay = yield Replay_1.Replay.parseCustomFile(buffer);
                this.replay.done(replay);
                this.events.emit('load', this.replay);
                this.checkStatus();
            }
            catch (err) {
                this.replay.error();
                this.events.emit('error', err, this.replay);
            }
        });
    }
    loadMapFromPath(filePath) {
        return __awaiter(this, void 0, void 0, function* () {
            this.clear();
            const name = filePath.split(/[/\\]/).pop() || filePath;
            const dir = filePath.replace(/[\/\\][^\/\\]+$/, '');
            this.map = new LoadItemBsp(name);
            this.events.emit('loadstart', this.map);
            try {
                this.map.progress = 0.5;
                this.events.emit('progress', this.map);
                const buffer = yield (0, TauriLoader_1.readFileBytes)(filePath);
                this.map.progress = 1;
                this.events.emit('progress', this.map);
                const map = yield BspParser_1.BspParser.parse(name, buffer);
                this.map.done(map);
                const resourceRoot = this.localResourceRoot || dir;
                map.entities
                    .map((entity) => typeof entity.model === 'string' && entity.model.includes('.spr') ? entity.model : undefined)
                    .filter((sprite, index, sprites) => sprite && sprites.indexOf(sprite) === index)
                    .forEach(sprite => sprite && this.loadSpriteFromPath(`${resourceRoot}/${sprite}`));
                const skyname = map.entities[0].skyname;
                if (skyname) {
                    const sides = ['bk', 'dn', 'ft', 'lf', 'rt', 'up'];
                    sides.forEach(side => this.loadSkyFromPath(`${resourceRoot}/gfx/env/${skyname}${side}.tga`, `${skyname}${side}`));
                }
                if (map.textures.find((a) => a.isExternal)) {
                    const wads = map.entities[0].wad;
                    const wadRoot = this.localResourceRoot || dir;
                    const wadPromises = wads.map((w) => this.loadWadFromPath(`${wadRoot}/${w}`));
                    yield Promise.all(wadPromises);
                }
                this.events.emit('load', this.map);
                this.checkStatus();
            }
            catch (err) {
                this.map.error();
                this.events.emit('error', err, this.map);
            }
        });
    }
    loadReplay(name) {
        return __awaiter(this, void 0, void 0, function* () {
            this.replay = new LoadItemReplay(name);
            this.events.emit('loadstart', this.replay);
            const progressCallback = (_1, progress) => {
                if (this.replay) {
                    this.replay.progress = progress;
                }
                this.events.emit('progress', this.replay);
            };
            const replayPath = this.config.getReplaysPath();
            const buffer = yield (0, Xhr_1.xhr)(`${replayPath}/${name}`, {
                method: 'GET',
                isBinary: true,
                progressCallback
            }).catch((err) => {
                if (this.replay) {
                    this.replay.error();
                }
                this.events.emit('error', err, this.replay);
            });
            if (this.replay.isError()) {
                return;
            }
            const replay = yield Replay_1.Replay.parseCustomFile(buffer);
            this.replay.done(replay);
            this.events.emit('load', this.replay);
            this.checkStatus();
        });
    }
    loadReplayFromUrl(url_1) {
        return __awaiter(this, arguments, void 0, function* (url, name = 'replay.rec') {
            this.replay = new LoadItemReplay(name);
            this.events.emit('loadstart', this.replay);
            const progressCallback = (_1, progress) => {
                if (this.replay) {
                    this.replay.progress = progress;
                }
                this.events.emit('progress', this.replay);
            };
            try {
                const buffer = yield (0, Xhr_1.xhr)(url, {
                    method: 'GET',
                    isBinary: true,
                    progressCallback
                });
                const replay = yield Replay_1.Replay.parseCustomFile(buffer);
                this.replay.done(replay);
                this.events.emit('load', this.replay);
                this.checkStatus();
            }
            catch (err) {
                this.replay.error();
                this.events.emit('error', err, this.replay);
            }
        });
    }
    loadReplayDataFromUrl(url) {
        return __awaiter(this, void 0, void 0, function* () {
            const buffer = yield (0, Xhr_1.xhr)(url, {
                method: 'GET',
                isBinary: true,
                progressCallback: () => { },
            });
            return Replay_1.Replay.parseCustomFile(buffer);
        });
    }
    loadMap(name) {
        return __awaiter(this, void 0, void 0, function* () {
            this.map = new LoadItemBsp(name);
            this.events.emit('loadstart', this.map);
            const progressCallback = (_1, progress) => {
                if (this.map) {
                    this.map.progress = progress;
                }
                this.events.emit('progress', this.map);
            };
            const mapsPath = this.config.getMapsPath();
            const buffer = yield (0, Xhr_1.xhr)(`${mapsPath}/${name}`, {
                method: 'GET',
                isBinary: true,
                progressCallback
            }).catch(err => {
                if (this.map) {
                    this.map.error();
                }
                this.events.emit('error', err, this.map);
            });
            if (this.map.isError()) {
                return;
            }
            const map = yield BspParser_1.BspParser.parse(name, buffer);
            this.map.done(map);
            map.entities
                .map((e) => {
                if (typeof e.model === 'string' && e.model.indexOf('.spr') > -1) {
                    return e.model;
                }
                return undefined;
            })
                .filter((a, pos, arr) => a && arr.indexOf(a) === pos)
                .forEach(a => a && this.loadSprite(a));
            const skyname = map.entities[0].skyname;
            if (skyname) {
                const sides = ['bk', 'dn', 'ft', 'lf', 'rt', 'up'];
                sides.map(a => `${skyname}${a}`).forEach(a => this.loadSky(a));
            }
            if (map.textures.find(a => a.isExternal)) {
                const wads = map.entities[0].wad;
                const wadPromises = wads.map((w) => this.loadWad(w));
                yield Promise.all(wadPromises);
            }
            this.events.emit('load', this.map);
            this.checkStatus();
        });
    }
    loadSprite(name) {
        return __awaiter(this, void 0, void 0, function* () {
            const item = new LoadItemSprite(name);
            this.sprites[name] = item;
            this.events.emit('loadstart', item);
            const progressCallback = (_1, progress) => {
                item.progress = progress;
                this.events.emit('progress', item);
            };
            const buffer = yield (0, Xhr_1.xhr)(`${this.config.getBasePath()}/${name}`, {
                method: 'GET',
                isBinary: true,
                progressCallback
            }).catch((err) => {
                item.error();
                this.events.emit('error', err, item);
                this.checkStatus();
            });
            if (item.isError()) {
                return;
            }
            const sprite = Sprite_1.Sprite.parse(buffer);
            item.done(sprite);
            this.events.emit('load', item);
            this.checkStatus();
        });
    }
    loadSpriteFromPath(filePath) {
        return __awaiter(this, void 0, void 0, function* () {
            const name = filePath.split(/[/\\]/).pop() || filePath;
            const item = new LoadItemSprite(name);
            this.sprites[name] = item;
            this.events.emit('loadstart', item);
            try {
                if (!(yield (0, TauriLoader_1.fileExists)(filePath))) {
                    item.skip();
                    this.checkStatus();
                    return;
                }
                const sprite = Sprite_1.Sprite.parse(yield (0, TauriLoader_1.readFileBytes)(filePath));
                item.done(sprite);
                this.events.emit('load', item);
                this.checkStatus();
            }
            catch (err) {
                item.error();
                this.events.emit('error', err, item);
                this.checkStatus();
            }
        });
    }
    loadSky(name) {
        return __awaiter(this, void 0, void 0, function* () {
            const item = new LoadItemSky(name);
            this.skies.push(item);
            this.events.emit('loadstart', item);
            const progressCallback = (_1, progress) => {
                item.progress = progress;
                this.events.emit('progress', item);
            };
            const skiesPath = this.config.getSkiesPath();
            const buffer = yield (0, Xhr_1.xhr)(`${skiesPath}/${name}.tga`, {
                method: 'GET',
                isBinary: true,
                progressCallback
            }).catch((err) => {
                console.log(`Missing ${skiesPath}/${name}.tga`);
                item.error();
                this.events.emit('error', err, item);
                this.checkStatus();
            });
            if (item.isError()) {
                return;
            }
            const skyImage = Tga_1.Tga.parse(buffer, name);
            item.done(skyImage);
            this.events.emit('load', item);
            this.checkStatus();
        });
    }
    loadSkyFromPath(filePath, name) {
        return __awaiter(this, void 0, void 0, function* () {
            const item = new LoadItemSky(name);
            this.skies.push(item);
            this.events.emit('loadstart', item);
            try {
                if (!(yield (0, TauriLoader_1.fileExists)(filePath))) {
                    item.skip();
                    this.checkStatus();
                    return;
                }
                item.done(Tga_1.Tga.parse(yield (0, TauriLoader_1.readFileBytes)(filePath), name));
                this.events.emit('load', item);
                this.checkStatus();
            }
            catch (err) {
                item.error();
                this.events.emit('error', err, item);
                this.checkStatus();
            }
        });
    }
    loadWad(name) {
        return __awaiter(this, void 0, void 0, function* () {
            const wadItem = new LoadItemWad(name);
            this.wads.push(wadItem);
            this.events.emit('loadstart', wadItem);
            const progressCallback = (_1, progress) => {
                wadItem.progress = progress;
                this.events.emit('progress', wadItem);
            };
            const wadsPath = this.config.getWadsPath();
            const buffer = yield (0, Xhr_1.xhr)(`${wadsPath}/${name}`, {
                method: 'GET',
                isBinary: true,
                progressCallback
            }).catch((err) => {
                console.log(`Missing ${wadsPath}/${name}.tga`);
                wadItem.error();
                this.events.emit('error', err, wadItem);
                this.checkStatus();
            });
            if (wadItem.isError()) {
                return;
            }
            const wad = yield Wad_1.Wad.parse(buffer);
            wadItem.done(wad);
            if (!this.map || !this.map.data) {
                return;
            }
            const map = this.map.data;
            const cmp = (a, b) => a.toLowerCase() === b.toLowerCase();
            wad.entries.forEach(entry => {
                if (entry.type !== 'texture') {
                    return;
                }
                map.textures.forEach(texture => {
                    if (cmp(entry.name, texture.name)) {
                        texture.width = entry.width;
                        texture.height = entry.height;
                        texture.data = entry.data;
                    }
                });
            });
            this.events.emit('load', wadItem);
            this.checkStatus();
        });
    }
    loadWadFromPath(filePath) {
        return __awaiter(this, void 0, void 0, function* () {
            const name = filePath.split(/[/\\]/).pop() || filePath;
            const wadItem = new LoadItemWad(name);
            this.wads.push(wadItem);
            this.events.emit('loadstart', wadItem);
            try {
                wadItem.progress = 0.5;
                this.events.emit('progress', wadItem);
                const exists = yield (0, TauriLoader_1.fileExists)(filePath);
                if (!exists) {
                    console.log(`Wad not found: ${filePath}`);
                    wadItem.skip();
                    this.checkStatus();
                    return;
                }
                const buffer = yield (0, TauriLoader_1.readFileBytes)(filePath);
                wadItem.progress = 1;
                this.events.emit('progress', wadItem);
                const wad = yield Wad_1.Wad.parse(buffer);
                wadItem.done(wad);
                if (!this.map || !this.map.data) {
                    return;
                }
                const map = this.map.data;
                const cmp = (a, b) => a.toLowerCase() === b.toLowerCase();
                wad.entries.forEach((entry) => {
                    if (entry.type !== 'texture') {
                        return;
                    }
                    map.textures.forEach((texture) => {
                        if (cmp(entry.name, texture.name)) {
                            texture.width = entry.width;
                            texture.height = entry.height;
                            texture.data = entry.data;
                        }
                    });
                });
                this.events.emit('load', wadItem);
                this.checkStatus();
            }
            catch (err) {
                console.log(`Failed to load wad: ${filePath}`, err);
                wadItem.skip();
                this.checkStatus();
            }
        });
    }
    loadSound(name, index) {
        return __awaiter(this, void 0, void 0, function* () {
            const sound = new LoadItemSound(name);
            this.sounds.push(sound);
            this.events.emit('loadstart', sound);
            const progressCallback = (_1, progress) => {
                sound.progress = progress;
                this.events.emit('progress', sound);
            };
            const soundsPath = this.config.getSoundsPath();
            const buffer = yield (0, Xhr_1.xhr)(`${soundsPath}/${name}`, {
                method: 'GET',
                isBinary: true,
                progressCallback
            }).catch((err) => {
                sound.error();
                this.events.emit('error', err, sound);
                this.checkStatus();
            });
            if (sound.isError()) {
                return;
            }
            const data = yield Sound_1.Sound.create(buffer).catch((err) => {
                sound.error();
                this.events.emit('error', err, sound);
                this.checkStatus();
            });
            if (!data || sound.isError()) {
                return;
            }
            data.index = index;
            data.name = name;
            sound.done(data);
            this.events.emit('load', sound);
            this.checkStatus();
        });
    }
}
exports.Loader = Loader;
